import { petCheckMode, petCheckBonuses, type PetEquipment } from './petEquipmentService.ts';
import type { PetCompanion } from '../domain/pet.ts';
import { statModForScore } from '../rules/statModifiers.ts';
import { opposedDifficulty } from '../rules/difficulty.ts';
import type { Degree } from '../rules/degrees.ts';
import type { Roller } from '../rules/dice.ts';
import type { RollMode } from '../rules/rollModes.ts';
import { resolveEvadeCheck } from '../rules/evade.ts';

export type PetCombatEntryOutcome = 'joined' | 'frenzied' | 'retry' | 'hidden';

/**
 * Guiding a bonded pet is an Int-Opposed Animal Handling Check. The crawler
 * rolls Animal Handling normally; the animal's Intelligence Mod supplies the
 * antagonist side of the opposed Difficulty.
 */
export function petHandlingDifficulty(pet: Pick<PetCompanion, 'unenhancedStats'>, floor: number, equipment?: PetEquipment): number {
  return opposedDifficulty(statModForScore(equipment?.enhancedStats.int ?? pet.unenhancedStats.int), floor).value;
}

/**
 * Rules p.232: Success joins combat; Amazing Success or better gives the pet
 * unrestricted use of both Actions; Standard Fail can retry; Major Fail or
 * worse makes it hide for the rest of the fight. Near Miss is still a failure,
 * but is not "Major Fail or worse", so it follows the retryable failure branch.
 */
export function petCombatEntryOutcome(degree: Degree): PetCombatEntryOutcome {
  if (degree === 'critical-hit' || degree === 'amazing-success') return 'frenzied';
  if (degree === 'standard-success') return 'joined';
  if (degree === 'major-fail' || degree === 'critical-fail') return 'hidden';
  return 'retry';
}

export function petAttackLimitForEntry(outcome: PetCombatEntryOutcome): 1 | 2 | null {
  if (outcome === 'joined') return 1;
  if (outcome === 'frenzied') return 2;
  return null;
}

export function petCombatEntryProblem(pet: Pick<PetCompanion, 'attitude' | 'healthBarSlotsRemaining'>): string | null {
  if (pet.healthBarSlotsRemaining <= 0) return 'A dead companion cannot enter combat.';
  if (pet.attitude !== 'bonded') return 'Only a Bonded companion can be guided into combat.';
  return null;
}

/** Pets roll Evade exactly as a crawler-style Evade Check: d20 + Dex Mod. */
export function rollPetEvade(roller: Roller, pet: Pick<PetCompanion, 'unenhancedStats'>, mode: RollMode = 'normal', equipment?: PetEquipment) {
  const context = { kind: 'evade', stat: 'dex', skillId: null } as const;
  const check = resolveEvadeCheck(roller, statModForScore(equipment?.enhancedStats.dex ?? pet.unenhancedStats.dex), petCheckMode(equipment, mode, context));
  const extra = [...petCheckBonuses(equipment, context), ...(equipment?.modifiers.evade ? [{ label: 'Pet equipment Evade', value: equipment.modifiers.evade }] : [])];
  return { ...check, components: [...check.components, ...extra], total: check.total + extra.reduce((sum, c) => sum + c.value, 0) };
}
